import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, Edit, Copy, Check, AlertCircle, XCircle, BellRing, Star, ShieldAlert, RotateCcw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { EmailAutoSettings } from "./EmailAutoSettings";

type TemplateType = 'reservation_confirmation' | 'reservation_modification' | 'reservation_cancellation' | 'reservation_reminder' | 'reservation_followup';

interface EmailTemplate {
  id: string;
  template_type: TemplateType;
  subject: string;
  body_html: string;
  include_table_name: boolean;
}

const platformNames: Record<string, string> = {
  google: 'Google',
  tripadvisor: 'TripAdvisor',
  yelp: 'Yelp',
  facebook: 'Facebook',
  trustpilot: 'Trustpilot',
  thefork: 'TheFork'
};

export const EmailSettingsView = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { establishmentId } = useAuth();
  const [activeTab, setActiveTab] = useState<TemplateType>('reservation_confirmation');
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [includeTableName, setIncludeTableName] = useState(false);
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState<{ subject: string; bodyHtml: string; includeTableName: boolean } | null>(null);

  // Récupérer les infos de l'établissement pour la prévisualisation (notamment le lien d'avis)
  const { data: establishment } = useQuery({
    queryKey: ["establishment-preview-data", establishmentId],
    queryFn: async () => {
      if (!establishmentId) return null;
      const { data, error } = await supabase
        .from("establishments")
        .select("name, preferred_review_platform, google_business_url, tripadvisor_review_url, yelp_review_url, facebook_review_url, trustpilot_review_url, thefork_review_url")
        .eq("id", establishmentId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!establishmentId,
  });

  // Récupérer les templates depuis la base de données
  const { data: templates, isLoading } = useQuery({
    queryKey: ["email-templates", establishmentId],
    queryFn: async () => {
      let query = supabase
        .from("email_templates")
        .select("*")
        .in("template_type", ["reservation_confirmation", "reservation_modification", "reservation_cancellation", "reservation_reminder", "reservation_followup"]);

      if (establishmentId) {
        query = query.eq('establishment_id', establishmentId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as EmailTemplate[];
    },
    enabled: !!establishmentId,
  });

  // Mettre à jour les états locaux quand le template change
  useEffect(() => {
    if (templates) {
      const template = templates.find(t => t.template_type === activeTab);
      if (template) {
        setSubject(template.subject);
        setBodyHtml(template.body_html);
        setIncludeTableName(template.include_table_name);
        setInitialData({
          subject: template.subject,
          bodyHtml: template.body_html,
          includeTableName: template.include_table_name
        });
        setHasChanges(false);
      }
    }
  }, [templates, activeTab]);

  // Détecter les changements
  useEffect(() => {
    if (initialData) {
      const changed = 
        subject !== initialData.subject || 
        bodyHtml !== initialData.bodyHtml || 
        includeTableName !== initialData.includeTableName;
      setHasChanges(changed);
    }
  }, [subject, bodyHtml, includeTableName, initialData]);

  // Mutation pour sauvegarder le template
  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      let query = supabase
        .from("email_templates")
        .update({
          subject,
          body_html: bodyHtml,
          include_table_name: includeTableName,
        })
        .eq("template_type", activeTab);

      if (establishmentId) {
        query = query.eq('establishment_id', establishmentId);
      }

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates", establishmentId] });
      toast({
        title: "✓ Template sauvegardé",
        description: "Le template d'email a été mis à jour avec succès.",
      });
      setHasChanges(false);
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: `Impossible de sauvegarder le template : ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Templates par défaut
  const getDefaultTemplate = (type: TemplateType): { subject: string; body_html: string } => {
    const defaults: Record<TemplateType, { subject: string; body_html: string }> = {
      reservation_confirmation: {
        subject: "Confirmation de votre réservation chez {{establishmentName}}",
        body_html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #333; border-bottom: 2px solid #E4A853; padding-bottom: 10px;">
    Votre réservation est confirmée
  </h2>
  
  <p style="font-size: 16px; color: #333;">Bonjour {{customerName}},</p>
  
  <p style="font-size: 16px; color: #555;">
    Nous avons le plaisir de vous confirmer votre réservation chez <strong>{{establishmentName}}</strong>.
  </p>
  
  <div style="background-color: #f8f9fa; border-radius: 8px; padding: 20px; margin: 25px 0;">
    <h3 style="margin: 0 0 15px 0; color: #333;">Détails de votre réservation</h3>
    <ul style="list-style: none; padding: 0; margin: 0;">
      <li style="padding: 10px 0; border-bottom: 1px solid #e9ecef;">
        📅 <strong>Date :</strong> {{reservationDate}}
      </li>
      <li style="padding: 10px 0; border-bottom: 1px solid #e9ecef;">
        🕐 <strong>Heure :</strong> {{reservationTime}}
      </li>
      <li style="padding: 10px 0;">
        👥 <strong>Nombre de personnes :</strong> {{numberOfPeople}}
      </li>
      {{tableName}}
    </ul>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{modifyLink}}" style="display: inline-block; padding: 14px 28px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 8px; margin: 0 8px 10px 8px; font-weight: 500;">
      Modifier ma réservation
    </a>
    <a href="{{cancelLink}}" style="display: inline-block; padding: 14px 28px; background-color: #ef4444; color: white; text-decoration: none; border-radius: 8px; margin: 0 8px 10px 8px; font-weight: 500;">
      Annuler ma réservation
    </a>
  </div>
  
  <p style="font-size: 16px; color: #555; margin-top: 30px;">
    Nous avons hâte de vous accueillir !
  </p>
  
  <p style="font-size: 16px; color: #555;">
    Chaleureusement,<br>
    <strong>L'équipe {{establishmentName}}</strong>
  </p>
</div>`
      },
      reservation_modification: {
        subject: "Modification confirmée - {{establishmentName}}",
        body_html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #333; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">
    Votre réservation a été modifiée
  </h2>
  
  <p style="font-size: 16px; color: #333;">Bonjour {{customerName}},</p>
  
  <p style="font-size: 16px; color: #555;">
    Nous confirmons la modification de votre réservation chez <strong>{{establishmentName}}</strong>.
  </p>
  
  <div style="background-color: #f0f9ff; border-radius: 8px; padding: 20px; margin: 25px 0; border-left: 4px solid #3b82f6;">
    <h3 style="margin: 0 0 15px 0; color: #333;">Nouvelles informations</h3>
    <ul style="list-style: none; padding: 0; margin: 0;">
      <li style="padding: 10px 0; border-bottom: 1px solid #e0f2fe;">
        📅 <strong>Date :</strong> {{reservationDate}}
      </li>
      <li style="padding: 10px 0; border-bottom: 1px solid #e0f2fe;">
        🕐 <strong>Heure :</strong> {{reservationTime}}
      </li>
      <li style="padding: 10px 0;">
        👥 <strong>Nombre de personnes :</strong> {{numberOfPeople}}
      </li>
      {{tableName}}
    </ul>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{modifyLink}}" style="display: inline-block; padding: 14px 28px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 8px; margin: 0 8px 10px 8px; font-weight: 500;">
      Modifier à nouveau
    </a>
    <a href="{{cancelLink}}" style="display: inline-block; padding: 14px 28px; background-color: #ef4444; color: white; text-decoration: none; border-radius: 8px; margin: 0 8px 10px 8px; font-weight: 500;">
      Annuler
    </a>
  </div>
  
  <p style="font-size: 16px; color: #555; margin-top: 30px;">
    À très bientôt !
  </p>
  
  <p style="font-size: 16px; color: #555;">
    Chaleureusement,<br>
    <strong>L'équipe {{establishmentName}}</strong>
  </p>
</div>`
      },
      reservation_cancellation: {
        subject: "Annulation confirmée - {{establishmentName}}",
        body_html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #333; border-bottom: 2px solid #ef4444; padding-bottom: 10px;">
    Votre réservation a été annulée
  </h2>
  
  <p style="font-size: 16px; color: #333;">Bonjour {{customerName}},</p>
  
  <p style="font-size: 16px; color: #555;">
    Nous confirmons l'annulation de votre réservation chez <strong>{{establishmentName}}</strong>.
  </p>
  
  <div style="background-color: #fef2f2; border-radius: 8px; padding: 20px; margin: 25px 0; border-left: 4px solid #ef4444;">
    <h3 style="margin: 0 0 15px 0; color: #333;">Réservation annulée</h3>
    <ul style="list-style: none; padding: 0; margin: 0;">
      <li style="padding: 10px 0; border-bottom: 1px solid #fee2e2;">
        📅 <strong>Date :</strong> {{reservationDate}}
      </li>
      <li style="padding: 10px 0; border-bottom: 1px solid #fee2e2;">
        🕐 <strong>Heure :</strong> {{reservationTime}}
      </li>
      <li style="padding: 10px 0;">
        👥 <strong>Nombre de personnes :</strong> {{numberOfPeople}}
      </li>
    </ul>
  </div>
  
  <p style="font-size: 16px; color: #555;">
    Nous espérons avoir le plaisir de vous accueillir une prochaine fois.
  </p>
  
  <p style="font-size: 16px; color: #555;">
    Chaleureusement,<br>
    <strong>L'équipe {{establishmentName}}</strong>
  </p>
</div>`
      },
      reservation_reminder: {
        subject: "Rappel : Votre réservation demain chez {{establishmentName}}",
        body_html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #333; border-bottom: 2px solid #E4A853; padding-bottom: 10px;">
    Rappel : Votre réservation est pour demain
  </h2>
  
  <p style="font-size: 16px; color: #333;">Bonjour {{customerName}},</p>
  
  <p style="font-size: 16px; color: #555;">
    Nous avons hâte de vous accueillir <strong>demain</strong> chez <strong>{{establishmentName}}</strong> !
  </p>
  
  <div style="background-color: #fffbeb; border-radius: 8px; padding: 20px; margin: 25px 0; border-left: 4px solid #E4A853;">
    <h3 style="margin: 0 0 15px 0; color: #333;">Votre réservation</h3>
    <ul style="list-style: none; padding: 0; margin: 0;">
      <li style="padding: 10px 0; border-bottom: 1px solid #fef3c7;">
        📅 <strong>Date :</strong> {{reservationDate}}
      </li>
      <li style="padding: 10px 0; border-bottom: 1px solid #fef3c7;">
        🕐 <strong>Heure :</strong> {{reservationTime}}
      </li>
      <li style="padding: 10px 0;">
        👥 <strong>Nombre de personnes :</strong> {{numberOfPeople}}
      </li>
      {{tableName}}
    </ul>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{modifyLink}}" style="display: inline-block; padding: 14px 28px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 8px; margin: 0 8px 10px 8px; font-weight: 500;">
      Modifier
    </a>
    <a href="{{cancelLink}}" style="display: inline-block; padding: 14px 28px; background-color: #ef4444; color: white; text-decoration: none; border-radius: 8px; margin: 0 8px 10px 8px; font-weight: 500;">
      Annuler
    </a>
  </div>
  
  <p style="font-size: 16px; color: #555;">
    À demain !
  </p>
  
  <p style="font-size: 16px; color: #555;">
    Chaleureusement,<br>
    <strong>L'équipe {{establishmentName}}</strong>
  </p>
</div>`
      },
      reservation_followup: {
        subject: "Merci pour votre visite chez {{establishmentName}}",
        body_html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #333; border-bottom: 2px solid #E4A853; padding-bottom: 10px;">
    Merci pour votre visite !
  </h2>
  
  <p style="font-size: 16px; color: #333;">Bonjour {{customerName}},</p>
  
  <p style="font-size: 16px; line-height: 1.6; color: #555;">
    Nous tenions à vous remercier sincèrement d'être venu nous rendre visite chez <strong>{{establishmentName}}</strong>.
  </p>
  
  <p style="font-size: 16px; line-height: 1.6; color: #555;">
    Nous espérons que vous avez passé un agréable moment et que tout était à la hauteur de vos attentes.
  </p>
  
  <p style="font-size: 16px; line-height: 1.6; color: #555;">
    Votre avis compte beaucoup pour nous ! Si vous avez apprécié votre expérience, nous vous serions très reconnaissants de prendre quelques instants pour partager votre avis.
  </p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{reviewLink}}" style="display: inline-block; padding: 16px 32px; background-color: #E4A853; color: white; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px;">
      ⭐ Partager mon avis sur {{reviewPlatformName}}
    </a>
  </div>
  
  <p style="font-size: 16px; line-height: 1.6; color: #555;">
    Merci encore pour votre confiance, et nous espérons avoir le plaisir de vous accueillir à nouveau très bientôt.
  </p>
  
  <p style="font-size: 16px; color: #555;">
    Chaleureusement,<br>
    <strong>L'équipe {{establishmentName}}</strong>
  </p>
</div>`
      }
    };
    return defaults[type];
  };

  // Mutation pour réinitialiser le template depuis le template maître
  const resetTemplateMutation = useMutation({
    mutationFn: async () => {
      // 1. Récupérer le template maître (establishment_id = NULL)
      const { data: masterTemplate, error: fetchError } = await supabase
        .from("email_templates")
        .select("subject, body_html, include_table_name")
        .eq("template_type", activeTab)
        .is("establishment_id", null)
        .single();

      if (fetchError || !masterTemplate) {
        throw new Error("Template maître introuvable. Veuillez contacter le super admin.");
      }

      // 2. Upsert le template (créer ou mettre à jour)
      const { error } = await supabase
        .from("email_templates")
        .upsert({
          establishment_id: establishmentId,
          template_type: activeTab,
          subject: masterTemplate.subject,
          body_html: masterTemplate.body_html,
          include_table_name: masterTemplate.include_table_name ?? false
        }, { 
          onConflict: 'establishment_id,template_type' 
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates", establishmentId] });
      toast({
        title: "✓ Template réinitialisé",
        description: "Le template a été remis à sa version par défaut (template maître).",
      });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: `Impossible de réinitialiser le template : ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const copyToClipboard = (variable: string) => {
    navigator.clipboard.writeText(variable);
    setCopiedVariable(variable);
    toast({
      title: "Variable copiée !",
      description: `${variable} a été copié dans le presse-papier`,
    });
    setTimeout(() => setCopiedVariable(null), 2000);
  };

  const requiredVariables = [
    "{{customerName}}",
    "{{reservationDate}}",
    "{{reservationTime}}",
    "{{numberOfPeople}}"
  ];

  const missingVariables = requiredVariables.filter(v => !bodyHtml.includes(v));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Calculer le vrai lien d'avis configuré pour l'établissement
  const getEstablishmentReviewUrl = (): string => {
    if (!establishment) return "https://g.page/r/xxx/review";
    const platform = establishment.preferred_review_platform || 'google';
    const urlMap: Record<string, string | null> = {
      google: establishment.google_business_url,
      tripadvisor: establishment.tripadvisor_review_url,
      yelp: establishment.yelp_review_url,
      facebook: establishment.facebook_review_url,
      trustpilot: establishment.trustpilot_review_url,
      thefork: establishment.thefork_review_url
    };
    return urlMap[platform] || "(aucun lien configuré)";
  };

  const getEstablishmentReviewPlatformName = (): string => {
    if (!establishment) return "Google";
    const platform = establishment.preferred_review_platform || 'google';
    return platformNames[platform] || platform;
  };

  // Données d'exemple pour l'aperçu (utilise les vraies données de l'établissement pour les avis)
  const previewData = {
    customerName: "Jean Dupont",
    reservationDate: "31/10/2025",
    reservationTime: "13:00",
    numberOfPeople: "4",
    tableName: '<li style="padding: 8px 0;">🪑 <strong>Table :</strong> Table 5</li>',
    confirmationNumber: "abc-123-def-456",
    modifyLink: "#",
    cancelLink: "#",
    reviewLink: getEstablishmentReviewUrl(),
    reviewPlatformName: getEstablishmentReviewPlatformName(),
    establishmentName: establishment?.name || "Restaurant Exemple",
  };

  const getPreviewHtml = () => {
    let preview = bodyHtml
      .replace(/\{\{customerName\}\}/g, previewData.customerName)
      .replace(/\{\{reservationDate\}\}/g, previewData.reservationDate)
      .replace(/\{\{reservationTime\}\}/g, previewData.reservationTime)
      .replace(/\{\{numberOfPeople\}\}/g, previewData.numberOfPeople)
      .replace(/\{\{confirmationNumber\}\}/g, previewData.confirmationNumber)
      .replace(/\{\{modifyLink\}\}/g, previewData.modifyLink)
      .replace(/\{\{cancelLink\}\}/g, previewData.cancelLink)
      .replace(/\{\{reviewLink\}\}/g, previewData.reviewLink)
      .replace(/\{\{reviewPlatformName\}\}/g, previewData.reviewPlatformName)
      .replace(/\{\{establishmentName\}\}/g, previewData.establishmentName);

    if (includeTableName) {
      preview = preview.replace(/\{\{tableName\}\}/g, previewData.tableName);
    } else {
      preview = preview.replace(/\{\{tableName\}\}/g, "");
    }

    return preview;
  };

  const isFollowupTab = activeTab === 'reservation_followup';
  
  const baseVariables = [
    { name: "{{customerName}}", description: "Nom complet du client", example: "Jean Dupont" },
    { name: "{{reservationDate}}", description: "Date de réservation", example: "31/10/2025" },
    { name: "{{reservationTime}}", description: "Heure de réservation", example: "13:00" },
    { name: "{{numberOfPeople}}", description: "Nombre de personnes", example: "4" },
    { name: "{{tableName}}", description: "Nom de la table (si activé)", example: "Table 5" },
    { name: "{{confirmationNumber}}", description: "Numéro de confirmation", example: "abc-123" },
    { name: "{{modifyLink}}", description: "Lien de modification", example: "#" },
    { name: "{{cancelLink}}", description: "Lien d'annulation", example: "#" },
    { name: "{{cancellationNotice}}", description: "Avertissement si annulation J interdite", example: "📌 Note..." },
    { name: "{{establishmentName}}", description: "Nom de l'établissement", example: "Restaurant Exemple" },
  ];
  
  const followupVariables = [
    { name: "{{customerName}}", description: "Nom complet du client", example: "Jean Dupont" },
    { name: "{{reviewLink}}", description: "Lien vers la plateforme d'avis", example: "https://g.page/..." },
    { name: "{{reviewPlatformName}}", description: "Nom de la plateforme d'avis", example: "Google" },
    { name: "{{establishmentName}}", description: "Nom de l'établissement", example: "Restaurant Exemple" },
  ];
  
  const variables = isFollowupTab ? followupVariables : baseVariables;

  return (
    <TooltipProvider>
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TemplateType)} className="w-full">
        <TabsList className="grid w-full max-w-4xl grid-cols-5 mb-6">
          <TabsTrigger value="reservation_confirmation" className="gap-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Confirmation</span>
          </TabsTrigger>
          <TabsTrigger value="reservation_modification" className="gap-2">
            <Edit className="h-4 w-4" />
            <span className="hidden sm:inline">Modification</span>
          </TabsTrigger>
          <TabsTrigger value="reservation_cancellation" className="gap-2">
            <XCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Annulation</span>
          </TabsTrigger>
          <TabsTrigger value="reservation_reminder" className="gap-2">
            <BellRing className="h-4 w-4" />
            <span className="hidden sm:inline">Rappel J-1</span>
          </TabsTrigger>
          <TabsTrigger value="reservation_followup" className="gap-2">
            <Star className="h-4 w-4" />
            <span className="hidden sm:inline">Suivi J+1</span>
          </TabsTrigger>
        </TabsList>

        <Alert variant="destructive" className="mb-6 border-destructive/50 bg-destructive/5">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Zone sensible</AlertTitle>
          <AlertDescription>
            Les modifications apportées aux templates d'emails seront immédiatement 
            appliquées à tous les emails envoyés à vos clients. Procédez avec prudence 
            et vérifiez toujours l'aperçu avant de sauvegarder.
          </AlertDescription>
        </Alert>

        <TabsContent value={activeTab} className="animate-fade-in">
          {/* Configuration automatique pour Rappel J-1 et Suivi J+1 */}
          {activeTab === 'reservation_reminder' && (
            <EmailAutoSettings establishmentId={establishmentId || undefined} type="reminder" />
          )}
          {activeTab === 'reservation_followup' && (
            <EmailAutoSettings establishmentId={establishmentId || undefined} type="followup" />
          )}
          
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Formulaire d'édition */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {activeTab === 'reservation_confirmation' ? (
                      <>
                        <Mail className="h-5 w-5" />
                        Template d'email de confirmation
                      </>
                    ) : activeTab === 'reservation_modification' ? (
                      <>
                        <Edit className="h-5 w-5" />
                        Template d'email de modification
                      </>
                    ) : activeTab === 'reservation_cancellation' ? (
                      <>
                        <XCircle className="h-5 w-5" />
                        Template d'email d'annulation
                      </>
                    ) : activeTab === 'reservation_reminder' ? (
                      <>
                        <BellRing className="h-5 w-5" />
                        Template d'email de rappel
                      </>
                    ) : (
                      <>
                        <Star className="h-5 w-5" />
                        Template d'email de suivi
                      </>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {activeTab === 'reservation_confirmation' 
                      ? "Personnalisez le contenu de l'email envoyé aux clients lors de leur réservation."
                      : activeTab === 'reservation_modification'
                      ? "Personnalisez le contenu de l'email envoyé aux clients lors de la modification de leur réservation."
                      : activeTab === 'reservation_cancellation'
                      ? "Personnalisez le contenu de l'email envoyé aux clients lors de l'annulation de leur réservation."
                      : activeTab === 'reservation_reminder'
                      ? "Personnalisez le contenu de l'email de rappel envoyé aux clients la veille de leur réservation."
                      : "Personnalisez le contenu de l'email de suivi envoyé le lendemain aux clients marqués comme arrivés."
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-sm font-medium">
                      Sujet de l'email *
                    </Label>
                    <Input
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Confirmation de votre réservation"
                      className="transition-all"
                    />
                    <p className="text-xs text-muted-foreground">
                      {subject.length} caractères
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bodyHtml" className="text-sm font-medium">
                      Corps de l'email (HTML) *
                    </Label>
                    <Textarea
                      id="bodyHtml"
                      value={bodyHtml}
                      onChange={(e) => setBodyHtml(e.target.value)}
                      placeholder="Contenu HTML de l'email"
                      rows={15}
                      className="font-mono text-sm transition-all"
                    />
                  </div>


                  {missingVariables.length > 0 && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning">
                      <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-warning mb-1">Variables manquantes</p>
                        <p className="text-muted-foreground">
                          {missingVariables.join(", ")}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => saveTemplateMutation.mutate()}
                      disabled={saveTemplateMutation.isPending || !hasChanges}
                      className="flex-1 transition-all"
                    >
                      {saveTemplateMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sauvegarde...
                        </>
                      ) : hasChanges ? (
                        "Sauvegarder les modifications"
                      ) : (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          À jour
                        </>
                      )}
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          disabled={resetTemplateMutation.isPending}
                          className="shrink-0"
                        >
                          {resetTemplateMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Réinitialiser le template ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Cette action remplacera le contenu actuel par le template par défaut de Piktable. 
                            Toutes vos personnalisations seront perdues.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => resetTemplateMutation.mutate()}
                          >
                            Réinitialiser
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Variables disponibles</CardTitle>
                  <CardDescription>
                    Cliquez sur une variable pour la copier dans le presse-papier
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {variables.map((variable) => (
                      <Tooltip key={variable.name}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => copyToClipboard(variable.name)}
                            className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors text-left group"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Badge variant="secondary" className="font-mono text-xs shrink-0">
                                {variable.name}
                              </Badge>
                              <span className="text-xs text-muted-foreground truncate">
                                {variable.description}
                              </span>
                            </div>
                            {copiedVariable === variable.name ? (
                              <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                            ) : (
                              <Copy className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p className="text-xs">Exemple : {variable.example}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Aperçu */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Aperçu de l'email</CardTitle>
                    <Badge variant="outline" className="gap-1">
                      <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                      Aperçu en direct
                    </Badge>
                  </div>
                  <CardDescription>
                    Voici comment l'email apparaîtra aux clients (avec des données d'exemple)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4 bg-background">
                    <div className="text-sm mb-2 p-2 bg-muted rounded">
                      <strong className="text-muted-foreground">Sujet :</strong>{" "}
                      <span className="text-foreground">{subject || "(vide)"}</span>
                    </div>
                    <Separator className="my-4" />
                    <div
                      dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                      className="prose prose-sm max-w-none dark:prose-invert transition-all"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </TooltipProvider>
  );
};
